import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  clientsAPI, casesAPI, trustAccountsAPI, invoicesAPI, notesAPI, employeesAPI,
} from '../services/api';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import {
  ArrowLeft, Edit2, Plus, Phone, Mail, MapPin, User,
  Briefcase, Shield, FileText, DollarSign, StickyNote,
  Download, Send, ChevronDown, MoreHorizontal, Zap,
} from 'lucide-react';
import { toast } from 'react-toastify';

const TABS = [
  { id: 'overview', label: 'Client Information', icon: User },
  { id: 'cases',    label: 'Cases',              icon: Briefcase },
  { id: 'trust',    label: 'Trust Accounts',     icon: Shield },
  { id: 'bills',    label: 'Bills',              icon: DollarSign },
  { id: 'notes',    label: 'Notes',              icon: StickyNote },
];

// Status groups for the filter tabs
const BILL_FILTERS = [
  { id: 'Draft',    label: 'Draft' },
  { id: 'Submitted', label: 'Pending Approval' },
  { id: 'Unpaid',   label: 'Unpaid' },   // Approved + Sent + Partially Paid + Overdue
  { id: 'Paid',     label: 'Paid' },
  { id: 'All',      label: 'All' },
  { id: 'Archived', label: 'Archive' },
];

const UNPAID_STATUSES = new Set(['Approved', 'Sent', 'Partially Paid', 'Overdue']);

function matchesBillFilter(inv, filter) {
  if (filter === 'All') return inv.status !== 'Archived';
  if (filter === 'Unpaid') return UNPAID_STATUSES.has(inv.status);
  return inv.status === filter;
}

// ── Action dropdown for a single invoice row ────────────────────────────────
function InvoiceActions({ inv, employees, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  const [approverId, setApproverId] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const act = async (fn, successMsg) => {
    try { await fn(); toast.success(successMsg); onRefresh(); }
    catch (err) { toast.error(err.response?.data?.error || 'Action failed'); }
    setOpen(false);
  };

  const handleDownload = async () => {
    try {
      const res = await invoicesAPI.pdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `INV-${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!approverId) return toast.error('Select an approver');
    try {
      await invoicesAPI.submitForApproval(inv.id, approverId);
      toast.success('Submitted for approval');
      setSubmitModal(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const canSend   = ['Draft', 'Approved', 'Submitted'].includes(inv.status);
  const canSubmit = ['Draft'].includes(inv.status);
  const canApprove = ['Submitted', 'Draft'].includes(inv.status);
  const canTrust  = !['Paid', 'Archived', 'Cancelled'].includes(inv.status);
  const canArchive = !['Archived'].includes(inv.status);

  return (
    <>
      <div className="flex items-center gap-1" ref={ref}>
        {/* Primary Send button */}
        <button
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 transition-colors"
          onClick={() => act(() => invoicesAPI.send(inv.id), 'Invoice sent to client')}
          disabled={!canSend}
          title={canSend ? 'Send to client' : 'Already sent'}
        >
          <Send size={12} /> Send
        </button>
        {/* Dropdown chevron */}
        <div className="relative">
          <button
            className="px-1.5 py-1.5 text-xs font-medium bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 transition-colors"
            onClick={() => setOpen(v => !v)}
          >
            <ChevronDown size={12} />
          </button>
          {open && (
            <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
              {canSubmit && (
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => { setOpen(false); setSubmitModal(true); }}>
                  Submit for approval
                </button>
              )}
              {canApprove && (
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.approve(inv.id), 'Invoice approved')}>
                  Approve
                </button>
              )}
              {canTrust && (
                <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.approveAndApplyTrust(inv.id), 'Paid from trust account')}>
                  Approve &amp; apply trust
                </button>
              )}
              <button className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => { setOpen(false); handleDownload(); }}>
                Download PDF
              </button>
              {canArchive && (
                <button className="w-full text-left px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
                  onClick={() => act(() => invoicesAPI.archive(inv.id), 'Invoice archived')}>
                  Archive
                </button>
              )}
              <div className="border-t border-gray-100 my-1" />
              <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => { if (window.confirm('Delete invoice?')) act(() => invoicesAPI.delete(inv.id), 'Deleted'); }}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Submit for approval modal */}
      <Modal isOpen={submitModal} onClose={() => setSubmitModal(false)} title="Submit for Approval">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Select the person who should approve invoice <strong>INV-{inv.invoice_number}</strong>.
          </p>
          <div>
            <label className="label">Approver *</label>
            <select className="input" required value={approverId} onChange={e => setApproverId(e.target.value)}>
              <option value="">— Select approver —</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.full_name} — {emp.role}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setSubmitModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary">Submit</button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client,    setClient]    = useState(null);
  const [cases,     setCases]     = useState([]);
  const [trust,     setTrust]     = useState([]);
  const [invoices,  setInvoices]  = useState([]);
  const [notes,     setNotes]     = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab,       setTab]       = useState('overview');
  const [billFilter, setBillFilter] = useState('All');
  const [loading,   setLoading]   = useState(true);
  const [editModal,  setEditModal]  = useState(false);
  const [noteModal,  setNoteModal]  = useState(false);
  const [form,      setForm]      = useState({});
  const [noteForm,  setNoteForm]  = useState({ subject: '', content: '', is_private: false });
  const [saving,    setSaving]    = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [clientRes, casesRes, trustRes, invRes, notesRes, empRes] = await Promise.all([
        clientsAPI.get(id),
        casesAPI.list({ client: id }),
        trustAccountsAPI.list({ client: id }),
        invoicesAPI.list({ client: id }),
        notesAPI.list({ client: id }),
        employeesAPI.list({ status: 'Active' }),
      ]);
      setClient(clientRes.data);
      setCases(casesRes.data.results || casesRes.data);
      setTrust(trustRes.data.results || trustRes.data);
      setInvoices(invRes.data.results || invRes.data);
      setNotes(notesRes.data.results || notesRes.data);
      setEmployees(empRes.data.results || empRes.data);
    } finally { setLoading(false); }
  };

  const reloadInvoices = async () => {
    const { data } = await invoicesAPI.list({ client: id });
    setInvoices(data.results || data);
  };

  useEffect(() => { load(); }, [id]);

  const handleEditClient = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await clientsAPI.update(id, form);
      toast.success('Client updated'); setEditModal(false); load();
    } catch { toast.error('Failed to update client'); }
    finally { setSaving(false); }
  };

  const handleAddNote = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await notesAPI.create({ ...noteForm, client: id });
      toast.success('Note added'); setNoteModal(false);
      setNoteForm({ subject: '', content: '', is_private: false }); load();
    } catch { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-96">
      <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (!client) return <div className="p-6 text-center text-gray-500">Client not found.</div>;

  const totalInvoiced = invoices.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);
  const totalPaid     = invoices.reduce((s, i) => s + parseFloat(i.paid_amount   || 0), 0);
  const trustBalance  = trust.reduce((s, a) => s + parseFloat(a.current_balance  || 0), 0);

  const filteredBills = invoices.filter(inv => matchesBillFilter(inv, billFilter));

  // Count per filter tab
  const billCount = (f) => invoices.filter(inv => matchesBillFilter(inv, f)).length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/clients')} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={18} />
          </button>
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xl font-bold">{client.client_name?.[0]}</span>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{client.client_name}</h1>
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">Client</span>
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-sm text-gray-500">{client.client_type}</span>
              <StatusBadge status={client.is_active ? 'Active' : 'Inactive'} />
              {client.email && (
                <a href={`mailto:${client.email}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                  <Mail size={12} />{client.email}
                </a>
              )}
              {client.phone && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone size={12} />{client.phone}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary text-sm flex items-center gap-1.5" onClick={() => setEditModal(true)}>
            <Edit2 size={14} /> Edit
          </button>
          <button className="btn-primary text-sm flex items-center gap-1.5"
            onClick={() => navigate('/invoices')}>
            <Zap size={14} /> Quick bill
          </button>
          <button className="btn-primary text-sm flex items-center gap-1.5"
            onClick={() => navigate('/cases', { state: { prefillClient: id } })}>
            <Plus size={14} /> New Case
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card text-center">
          <p className="text-2xl font-bold text-blue-700">{cases.filter(c => c.status === 'Open').length}</p>
          <p className="text-xs text-gray-500 mt-1">Open Cases</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-amber-600">
            US${(totalInvoiced - totalPaid).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Outstanding Balance</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-purple-600">
            US${trustBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Trust Funds</p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <t.icon size={15} />{t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Overview ── */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
            <InfoRow label="Full Name"      value={client.client_name} />
            <InfoRow label="Type"           value={client.client_type} />
            <InfoRow label="Email"          value={client.email} />
            <InfoRow label="Phone"          value={client.phone} />
            <InfoRow label="Address"        value={client.address} />
            <InfoRow label="Billing Method" value={client.billing_method} />
          </div>
          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900 mb-2">Personal Details</h3>
            <InfoRow label="ID Number"           value={client.id_number} />
            <InfoRow label="Tax ID"              value={client.tax_id} />
            <InfoRow label="Gender"              value={client.gender} />
            <InfoRow label="Date of Birth"       value={client.date_of_birth} />
            <InfoRow label="Contact First Name"  value={client.contact_first_name} />
            <InfoRow label="Contact Last Name"   value={client.contact_last_name} />
          </div>
        </div>
      )}

      {/* ── Cases ── */}
      {tab === 'cases' && (
        <div className="card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Cases ({cases.length})</h3>
            <button className="btn-primary text-sm flex items-center gap-1.5" onClick={() => navigate('/cases')}>
              <Plus size={14} /> New Case
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Case Title', 'Type', 'Attorney', 'Status', 'Opened'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cases.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">No cases yet</td></tr>
              ) : cases.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/cases/${c.id}`)}>
                  <td className="px-4 py-3 font-medium text-gray-900">{c.case_title}</td>
                  <td className="px-4 py-3 text-gray-500">{c.case_type}</td>
                  <td className="px-4 py-3 text-gray-500">{c.attorney_name || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-gray-500">{c.date_opened}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Trust ── */}
      {tab === 'trust' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {trust.length} trust account(s) · Total: <strong>US${trustBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            </p>
            <button className="btn-primary text-sm flex items-center gap-1.5" onClick={() => navigate('/trust')}>
              <Plus size={14} /> New Trust Account
            </button>
          </div>
          {trust.length === 0 ? (
            <div className="card text-center py-10 text-gray-400">No trust accounts for this client.</div>
          ) : trust.map(a => (
            <div key={a.id} className="card flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{a.bank_name}</p>
                <p className="text-xs text-gray-500">Acct: {a.account_number}{a.branch_code && ` · Branch: ${a.branch_code}`}</p>
                {a.last_reconciliation_date && <p className="text-xs text-gray-400 mt-1">Last reconciled: {a.last_reconciliation_date}</p>}
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-purple-700">
                  US${parseFloat(a.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
                <button className="text-xs text-blue-600 hover:underline mt-1" onClick={() => navigate('/trust')}>
                  View transactions →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Bills ── */}
      {tab === 'bills' && (
        <div>
          {/* Filter tabs */}
          <div className="flex items-center gap-1 mb-4 flex-wrap">
            {BILL_FILTERS.map(f => {
              const count = billCount(f.id);
              return (
                <button
                  key={f.id}
                  onClick={() => setBillFilter(f.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    billFilter === f.id
                      ? 'bg-blue-700 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {f.label}{count > 0 && <span className="ml-1.5 text-xs opacity-75">({count})</span>}
                </button>
              );
            })}
            <div className="ml-auto">
              <input
                className="input w-44 text-sm py-1.5"
                placeholder="Search by ID…"
              />
            </div>
          </div>

          {/* Invoice table */}
          <div className="card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-36">Actions</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Last Sent</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Due</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Matter(s)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBills.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                    No {billFilter === 'All' ? '' : billFilter.toLowerCase()} invoices.
                  </td></tr>
                ) : filteredBills.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <InvoiceActions inv={inv} employees={employees} onRefresh={reloadInvoices} />
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {inv.sent_at
                        ? new Date(inv.sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : <span className="text-gray-300">Not sent yet</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-blue-700 hover:underline cursor-pointer">
                        {inv.invoice_number}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={
                        inv.status === 'Submitted' ? 'Pending Approval'
                        : inv.status === 'Sent' ? 'Unpaid'
                        : inv.status
                      } />
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {inv.due_date ? `Due ${new Date(inv.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : '—'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      US${parseFloat(inv.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3">
                      {inv.case_title ? (
                        <span className="text-xs text-blue-600 hover:underline cursor-pointer">
                          {inv.case_number ? `${inv.case_number}-` : ''}{inv.case_title}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Notes ── */}
      {tab === 'notes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{notes.length} note(s)</p>
            <button className="btn-primary text-sm flex items-center gap-1.5" onClick={() => setNoteModal(true)}>
              <Plus size={14} /> Add Note
            </button>
          </div>
          {notes.length === 0 ? (
            <div className="card text-center py-10 text-gray-400">No notes yet.</div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Subject', 'Note', 'Date', 'Author'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {notes.map(n => (
                    <tr key={n.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{n.subject || <span className="text-gray-400 italic">No subject</span>}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-sm"><p className="truncate">{n.content}</p></td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(n.created_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{n.employee_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Client Modal */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} title="Edit Client">
        <form onSubmit={handleEditClient} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Client / Company Name *</label>
            <input className="input" required value={form.client_name || ''} onChange={e => setForm({ ...form, client_name: e.target.value })} />
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input" value={form.client_type || 'Individual'} onChange={e => setForm({ ...form, client_type: e.target.value })}>
              {['Individual', 'Company', 'Trust', 'Government', 'Non-Profit'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Billing Method</label>
            <select className="input" value={form.billing_method || 'Hourly'} onChange={e => setForm({ ...form, billing_method: e.target.value })}>
              {['Hourly', 'Fixed Fee', 'Retainer', 'Contingency'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Address</label>
            <textarea className="input" rows={2} value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <label className="label">ID Number</label>
            <input className="input" value={form.id_number || ''} onChange={e => setForm({ ...form, id_number: e.target.value })} />
          </div>
          <div>
            <label className="label">Tax ID</label>
            <input className="input" value={form.tax_id || ''} onChange={e => setForm({ ...form, tax_id: e.target.value })} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </Modal>

      {/* Add Note Modal */}
      <Modal isOpen={noteModal} onClose={() => setNoteModal(false)} title="Add Note">
        <form onSubmit={handleAddNote} className="space-y-4">
          <div>
            <label className="label">Subject *</label>
            <input className="input" required value={noteForm.subject} onChange={e => setNoteForm({ ...noteForm, subject: e.target.value })} />
          </div>
          <div>
            <label className="label">Note *</label>
            <textarea className="input" rows={5} required value={noteForm.content} onChange={e => setNoteForm({ ...noteForm, content: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="np_private" checked={noteForm.is_private} onChange={e => setNoteForm({ ...noteForm, is_private: e.target.checked })} className="rounded" />
            <label htmlFor="np_private" className="text-sm text-gray-700">Private note</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setNoteModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Note'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs text-gray-400 w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-800">{value}</span>
    </div>
  );
}
